/* eslint-disable no-magic-numbers */

import { FinancialConfig } from '@futrli-sdk/predict';
import { FreestyleMethodCellData, FreestyleMethodRowData } from '../../../ui/data-builder/freestyle-content-form/types';
import { getDatesBetweenTodayAndNYears, forceLocalDate } from '../../../utils/dates';
import { getFYStartEnd } from '../../../utils/financial-year-end';
import {
  FreestyleNonFinancialDriver,
  FreestyleNonFinancialDriverSaveData
} from '../../../core/api/types/non-financial-drivers';

const DEFAULT_YEARS_IN_PAST = 2;

/**
 * Will return the earliest and latest financial years that given driver has data for (returned as an array of two numbers)
 * Will return array of undefined if the driver has no amount data
 * @param financialConfig - Financial config for the organisation
 * @param driver - The driver with the amounts in it
 */
export const getEarliestAndLatestFinancialYears = (
  financialConfig: FinancialConfig,
  driver: FreestyleNonFinancialDriver
) => {
  if (!Object.keys(driver.amounts).length) {
    return [undefined, undefined];
  }

  const sortedDates = Object.keys(driver.amounts)
    .map(dateStr => forceLocalDate(dateStr))
    .sort((a, b) => a.getTime() - b.getTime());

  const earliestAndLatestDates = [sortedDates[0], sortedDates[sortedDates.length - 1]];

  return earliestAndLatestDates.map(date => {
    const fyDates = getFYStartEnd(financialConfig, date.getFullYear());
    const financialYearEnd = forceLocalDate(fyDates.end);
    return date < financialYearEnd ? date.getFullYear() : date.getFullYear() + 1;
  });
};

/**
 * Will take driver data and convert it to the format needed to use the freestyle driver builder.
 * A lot of the buisness logic related to this component is used to generate this data.
 * @param driver - data to convert
 */
export const convertExistingDriver = (
  instanceId: number,
  financialConfig: FinancialConfig,
  driver: FreestyleNonFinancialDriver
) => {
  const dateParts = financialConfig.current_year.end.split('-');
  const currentYear = Number(dateParts[0]);
  const [minDataYear = currentYear, maxDataYear = currentYear] = getEarliestAndLatestFinancialYears(
    financialConfig,
    driver
  );
  const startYear = Math.min(minDataYear, currentYear - DEFAULT_YEARS_IN_PAST);
  const endYear = Math.max(maxDataYear, currentYear);
  const output: FreestyleMethodRowData[] = [];

  // Generate full financial years (doing it this way avoids issues with incomplete data sets)
  for (let i = startYear; i <= endYear; i++) {
    output.push(generateFinancialYearData(instanceId, financialConfig, i));
  }

  // build a object of dates to values so we can easily reference them in a moment
  const newAmounts: Record<string, number | null> = {};
  Object.keys(driver.amounts)
    .map(key => ({ key, value: driver.amounts[key] }))
    .forEach(amount => {
      newAmounts[amount.key] = !amount.value ? null : parseFloat(amount.value);
    });

  // Now loop through the amounts for our generated financial years and populate any values
  output.forEach(financialYear => {
    financialYear.months.forEach(month => {
      month.value = String(newAmounts[month.date] ?? '');
    });
  });

  return output;
};

/**
 * Updates all of the totals in the given FreestyleMethodRowData
 * @param data - FreestyleMethodRowData to update
 */
export const updateTotals = (data: FreestyleMethodRowData) => {
  data.isAllInPast = data.months.filter(m => m.isInPast).length === data.months.length;
  data.isAllReadOnly = data.months.filter(m => m.readOnly).length === data.months.length;
  data.isAllSelected = data.months.filter(m => m.selected || m.readOnly).length === data.months.length;
};

/**
 * Generates all data for a new financial year row for the given year
 * @param instanceId - Driver instance id
 * @param financialConfig - Financial config for the organisation
 * @param year - The year to generate the row for (2022 would be FY21-22)
 */
export const generateFinancialYearData = (
  instanceId: string | number,
  financialConfig: FinancialConfig,
  year: number | string
) => {
  const financialYearDates = getFYStartEnd(financialConfig, year);
  const financialYearStart = forceLocalDate(financialYearDates.start);
  const monthDates = getDatesBetweenTodayAndNYears(1, financialYearStart).slice(0, -1);
  const isCurrentYear = year.toString() === financialConfig.current_year.end.split('-')[0];

  const newYearData: FreestyleMethodRowData = {
    date: year.toString(),
    isAllInPast: false,
    isAllReadOnly: false,
    isAllSelected: false,
    isCurrentYear,
    driverInstanceId: instanceId,
    months: monthDates.map(d => {
      const date = d.substring(0, d.length - 3); // "2022-03-01" -> "2022-03" (removing day from string)
      const localDate = forceLocalDate(date);
      const now = new Date();

      const cell: FreestyleMethodCellData = {
        date,
        isInPast: localDate < now,
        localDate,
        readOnly: false,
        value: '',
        selected: false
      };

      return cell;
    })
  };

  updateTotals(newYearData);
  return newYearData;
};

/**
 * Generates the initial row data to display for a new driver, which includes the current financial
 * year and a number of previous years
 * @param instanceId - driver instance id
 * @param financialConfig - Financial config for the organisation
 * @param yearsInPast - The amount of past years to generate. Defaults to 2
 */
export const generateNewDriver = (
  instanceId: number,
  financialConfig: FinancialConfig,
  yearsInPast = DEFAULT_YEARS_IN_PAST
) => {
  const dateParts = financialConfig.current_year.end.split('-');
  const output: FreestyleMethodRowData[] = [];
  const currentYear = Number(dateParts[0]);
  const startYear = currentYear - yearsInPast;

  // currentYear + 1 because initially we want to show current year and year after current year
  for (let i = startYear; i <= currentYear + 1; i++) {
    output.push(generateFinancialYearData(instanceId, financialConfig, i));
  }

  return output;
};

/**
 * Returns a FreestyleNonFinancialDriverSaveData amounts object generated from the given FreestyleMethodRowData[]
 * @param rows - FreestyleMethodRowData array to convert. All years and months will be included.
 */
export const getAmountsFromRows = (rows: FreestyleMethodRowData[]) => {
  const output: FreestyleNonFinancialDriverSaveData['amounts'] = {};

  const amounts = rows.flatMap(row =>
    row.months.map(month => ({
      key: month.date,
      value: month.value || null
    }))
  );

  amounts.forEach(amount => {
    output[amount.key] = amount.value;
  });

  return output;
};
